import React from 'react';
import { useI18n } from '../../i18n/I18nContext';
import { statusLabel } from '../../utils/labels';
import './StatusBadge.css';

/**
 * Maps a domain-specific status value to a semantic tone.
 * Extend the map as new statuses appear; unknown values fall through to 'neutral'.
 */
const TONE_MAP = {
  // success
  uploaded: 'success',
  generated: 'success',
  active: 'success',
  completed: 'success',
  passed: 'success',
  paid: 'success',
  answered: 'success',
  approved: 'success',
  issued: 'success',

  // warning
  pending: 'warning',
  draft: 'warning',
  open: 'warning',
  unanswered: 'warning',
  partial: 'warning',
  invited: 'warning',
  suspended: 'warning',

  // info
  'in-progress': 'info',
  in_progress: 'info',
  inprogress: 'info',
  enrolled: 'info',
  scheduled: 'info',
  prospect: 'info',
  reserved: 'info',

  // danger
  failed: 'danger',
  rejected: 'danger',
  cancelled: 'danger',
  expired: 'danger',
  inactive: 'danger',
  churned: 'danger',
  revoked: 'danger',
};

export function statusToTone(value) {
  if (!value) return 'neutral';
  const key = String(value).toLowerCase().replace(/\s+/g, '-');
  return TONE_MAP[key] || 'neutral';
}

/**
 * Small status pill. Pass either an explicit `tone` or a raw `status` string
 * which is auto-mapped via {@link statusToTone}.
 *
 * With no children the label is the status word in the user's language
 * (`status.<key>` in i18n/ns/status.js: "Faol", "Tugallangan", "Qaytarilgan"), never the raw
 * enum. Pages that already hold their own translated label pass it as children.
 *
 *   <StatusBadge status={lesson.videoUrl ? 'uploaded' : 'pending'} />
 *   <StatusBadge tone="danger">{label}</StatusBadge>   // a label the page already translated
 *   <StatusBadge status="ACTIVE" dot />
 *
 * Tones: success, warning, danger, info, purple, teal, coral, brand, neutral.
 */
function StatusBadge({ tone, status, children, className = '', dot = false }) {
  const { t } = useI18n();
  const resolvedTone = tone || statusToTone(status);
  const text = children ?? (status != null ? statusLabel(t, status) : status);

  return (
    <span className={`status-badge tone-${resolvedTone} ${className}`.trim()}>
      {dot && <span className="status-badge-dot" aria-hidden="true" />}
      {typeof text === 'string'
        ? text.charAt(0).toUpperCase() + text.slice(1)
        : text}
    </span>
  );
}

export default StatusBadge;
